import { FC, createContext, useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { parseJwt, touchSessionActivity } from '../shared/api';
import { getStaffToken, isStaffAdminHost } from '../adminHost';
import { isSessionIdle, markSessionActivity } from '../shared/sessionActivity';

interface AuthContextType {
  loggedIn: boolean;
  user: any | null;
  signIn: (email: string, password: string) => Promise<void>;
  refresh: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function isTenantAuthExempt(pathname: string) {
  if (isStaffAdminHost()) return true;
  if (pathname.startsWith('/admin/sign-in') || pathname.startsWith('/admin/oauth')) return true;
  if (getStaffToken() && pathname.startsWith('/admin')) return true;
  return false;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { loggedIn = false, user, signIn, refresh, initializeUser, logout } = useAuthStore();
  const refreshTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const initialCheckDoneRef = useRef(false);

  // Function to check token expiration and schedule refresh
  const scheduleTokenRefresh = () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    const currentPath = window.location.pathname;
    const isPublicRoute = isTenantAuthExempt(currentPath) || [
      '/sign-in',
      '/sign-up',
      '/confirm-account',
      '/forgot-password',
      '/reset-password',
      '/mfa-verify',
      '/oauth-success',
      '/oauth-error',
      '/privacy-policy',
      '/terms-service',
    ].some(route => currentPath.startsWith(route));

    try {
      const decoded = parseJwt(token);
      if (!decoded || !decoded.exp) return;

      const expiryTime = decoded.exp * 1000;
      const currentTime = Date.now();
      const timeUntilExpiry = expiryTime - currentTime;
      const refreshBuffer = 5 * 60 * 1000; // 5 minutes

      // Clear any existing timeout
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }

      // If token is already expired or will expire soon, refresh immediately
      if (timeUntilExpiry <= refreshBuffer) {
        refresh().catch(error => {
          console.error('Token refresh failed:', error);
          // Only redirect to sign-in if not on a public route
          if (!isPublicRoute) {
            window.location.href = '/sign-in';
          }
        });
        return;
      }

      // Schedule refresh for 5 minutes before expiry
      const refreshTime = timeUntilExpiry - refreshBuffer;
      refreshTimeoutRef.current = setTimeout(() => {
        refresh().catch(error => {
          console.error('Scheduled token refresh failed:', error);
          // Only redirect to sign-in if not on a public route
          if (!isPublicRoute) {
            window.location.href = '/sign-in';
          }
        });
      }, refreshTime);

    } catch (error) {
      console.error('Error scheduling token refresh:', error);
      // Only redirect to sign-in if not on a public route
      if (!isPublicRoute) {
        window.location.href = '/sign-in';
      }
    }
  };

  // Initialize user session
  useEffect(() => {
    console.log('[AuthContext] useEffect triggered, initialCheckDone:', initialCheckDoneRef.current);
    
    const currentPath = location.pathname;
    const isPublicRoute = isTenantAuthExempt(currentPath) || [
      '/sign-in',
      '/sign-up',
      '/confirm-account',
      '/forgot-password',
      '/reset-password',
      '/mfa-verify',
      '/oauth-success',
      '/oauth-error',
      '/privacy-policy',
      '/terms-service',
    ].some(route => currentPath.startsWith(route));
    
    console.log('[AuthContext] Is public route?', isPublicRoute);
    
    // CRITICAL: If on public route, skip ALL auth checks immediately
    if (isPublicRoute) {
      console.log('[AuthContext] ✅ On public route:', currentPath, '- skipping ALL auth checks');
      return;
    }
    
    // Only run auth initialization for protected routes
    const init = async () => {
      try {
        if (!initialCheckDoneRef.current) {
          console.log('[AuthContext] First initialization, checking tokens...');
          initialCheckDoneRef.current = true;
          const token = localStorage.getItem('accessToken');
          const refreshToken = localStorage.getItem('refreshToken');
          
          console.log('[AuthContext] Has tokens?', { hasAccessToken: !!token, hasRefreshToken: !!refreshToken });
          
          if (token && refreshToken) {
            if (isSessionIdle()) {
              logout();
              window.location.href = '/sign-in?sso_error=' + encodeURIComponent('Your session ended after a period of inactivity.');
              return;
            }
            console.log('[AuthContext] Initializing user with existing tokens...');
            await initializeUser();
            scheduleTokenRefresh();
          } else {
            console.log('[AuthContext] ❌ No tokens found, redirecting to sign-in');
            window.location.href = '/sign-in';
          }
        } else {
          console.log('[AuthContext] Already initialized, skipping');
        }
      } catch (error) {
        console.error('[AuthContext] ❌ Failed to initialize user:', error);
        console.log('[AuthContext] Error occurred on protected route, redirecting to sign-in');
        window.location.href = '/sign-in';
      }
    };
    
    init();

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]); // Re-run when location changes

  useEffect(() => {
    if (!loggedIn) return undefined;
    const mark = () => {
      markSessionActivity();
      touchSessionActivity();
    };
    const events = ['pointerdown', 'keydown', 'scroll'] as const;
    events.forEach((event) => window.addEventListener(event, mark, { passive: true }));
    const timer = window.setInterval(() => {
      if (!isSessionIdle()) return;
      logout();
      window.location.href = '/sign-in?sso_error=' + encodeURIComponent('Your session ended after a period of inactivity.');
    }, 15000);
    return () => {
      events.forEach((event) => window.removeEventListener(event, mark));
      window.clearInterval(timer);
    };
  }, [loggedIn, logout]);

  // Schedule refresh after successful sign-in or refresh
  useEffect(() => {
    if (loggedIn) {
      scheduleTokenRefresh();
    }
  }, [loggedIn, user]);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && loggedIn) {
        scheduleTokenRefresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loggedIn]);

  return (
    <AuthContext.Provider value={{ loggedIn, user, signIn, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
}; 